// AutoPopTextField.js
import React, { useEffect, useState } from "react";
import {
    TextField, FormControl, InputLabel,
    InputAdornment, IconButton,
    Popover,
    Box,
    OutlinedInput
} from "@mui/material";
import { styled } from '@mui/material/styles';
import ClearIcon from '@mui/icons-material/Clear';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { AppStyle, AppWord } from '../value/AppConst';

const ListBox = styled(Box)({
    overflowY: 'auto',
});

const ItemBox = styled(Box)({
    fontSize: '0.85rem', padding: '6px 8px', cursor: 'pointer', borderBottom: '1px solid #dddddd',
    '&:hover': {
        backgroundColor: '#eeeeee',
    },
    '&:last-of-type': {
        borderBottom: 'none',
    },
});

function findItem(list, value, valueKey) {
    if(!list) return null;
    for(let i=0; i<list.length; i++) {
        if(list[i][valueKey] === value) {
            return list[i];
        }
    }
    return null;
}

/**
 * Auto complete text field with pop-up selection button.
 * 기존 AutoTextField는 좁은 뷰에서는 바깥의 뷰보다 넓게 펼쳐져서 스크롤이 생기고 아주 흉해짐.
 * 버튼을 누르면 팝업으로 Select 컴포넌트가 나타나도록 함.
 * 버튼: 선택된 값 또는 
 */
export default function AutoPopTextField({
    dict, // {id, label, ...., valueKey, labelKey}
    value,
    list,
    onChange,
    onSelectRecord,
    disabled,
    hideLabel,
    fullWidth,
    maxListHeight=400,
    nullable,
}) {
    const [labelSelected, setLabelSelected] = useState('');
    const [listDict, setListDict] = useState({}); // {valueKey, labelKey}
    const [popAnchorEl, setPopAnchorEl] = useState(null);
    const [searchText, setSearchText] = useState('');
    const [filteredList, setFilteredList] = useState([]);

    useEffect(()=>{
        const valueKey = dict.valueKey || dict.id;
        const labelKey = dict.labelKey || 'label';
        setListDict({valueKey: valueKey, labelKey: labelKey});
    }, [dict]);

    useEffect(()=>{
        if(!dict) {
            console.error('앱 오류: 입력데이터 정의가 제공되지 않았습니다.');
            return;
        }
        if(!list) {
            console.error('앱 오류: 목록이 제공되지 않았습니다.');
            return;
        }

        if(value !== undefined && value !== null && value !== '') {
            const item = findItem(list, value, listDict.valueKey);
            if(item) {
                if(dict.getOptionLabel) setLabelSelected(dict.getOptionLabel(item));
                else setLabelSelected(item[listDict.labelKey]);
            }
            else setLabelSelected(`${value}(?)`);
        }
        else {
            setLabelSelected('');
        }
    }, [listDict, value, list, dict]);

    useEffect(()=>{
        if(searchText && list) {
            const filtered = list.filter((item)=>{
                const label = dict.getOptionLabel ? dict.getOptionLabel(item) : item[listDict.labelKey || 'label'];
                return label && label.toLowerCase().includes(searchText.toLowerCase());
            });
            setFilteredList(filtered);
        }
        else {
            setFilteredList(list || []);
        }
    }, [searchText, list, listDict, dict]);


    const onChangeData = (item) => {
        if(item) {
            onChange(dict.id, item[listDict.valueKey], false);
        }
        else {
            onChange(dict.id, null, false);
        }
        if(onSelectRecord) {
            onSelectRecord(item);
        }
        setPopAnchorEl(null);
    };

    const onKeyUpInput = (event) => {
        if(disabled) return;
        // enter, up, down : open popover
        // escape : close popover
        if(event.key === 'Enter' || event.key === 'ArrowUp' || event.key === 'ArrowDown') {
            if(!disabled) setPopAnchorEl(event.currentTarget);
        }
        else if(event.key === 'Escape') {
            setPopAnchorEl(null);
        }
    };

    const InputID = 'auto-pop-text-input' + (dict.id ? '-' + dict.id : '');
    const SelectId = 'auto-pop-text-select' + (dict.id ? '-' + dict.id : '');
    
    return(
        <FormControl size={AppWord.SMALL} variant={AppStyle.mui.outlined}
            fullWidth={fullWidth}
        >
            {
                hideLabel ? null :
                <InputLabel htmlFor={InputID}
                    style={{
                        backgroundColor:'white', paddingTop:0
                    }}
                >
                    {dict.label||'선택'}
                </InputLabel>
            }
            <OutlinedInput id={InputID}
                value={labelSelected}
                size={AppWord.SMALL}
                readOnly={true}
                disabled={disabled}
                fullWidth={fullWidth}
                endAdornment={
                    <InputAdornment position='end'>
                        {
                            Boolean(value) && nullable &&
                            <IconButton
                                onClick={(e)=>{onChangeData(null); e.stopPropagation();}}
                                size={AppWord.SMALL}
                                edge='end'
                                disabled={disabled}
                            >
                                <ClearIcon />
                            </IconButton>
                        }
                        <ArrowDropDownIcon />
                    </InputAdornment>
                }
                onClick={(event)=>{
                    if(!disabled) setPopAnchorEl(event.currentTarget);
                }}
                onKeyUp={onKeyUpInput}
            />
            <Popover
                id={SelectId}
                open={Boolean(popAnchorEl)}
                anchorEl={popAnchorEl}
                onClose={()=>{setPopAnchorEl(null)}}
            >
                <Box style={{width: popAnchorEl ? popAnchorEl.clientWidth : 'auto'}}>
                    <TextField
                        size={AppWord.SMALL}
                        placeholder="검색어를 입력하세요."
                        variant={AppStyle.mui.outlined}
                        fullWidth={true}
                        onChange={(e)=>{setSearchText(e.target.value);}}
                    />
                    <ListBox
                        style={{maxHeight: maxListHeight}}
                    >
                        {
                            filteredList.map((item, index)=>(
                                <ItemBox
                                    key={index}
                                    onClick={()=>{onChangeData(item);}}
                                >
                                    {
                                        dict.getOptionLabel
                                        ? dict.getOptionLabel(item)
                                        : item[listDict.labelKey]
                                    }
                                </ItemBox>
                            ))
                        }
                    </ListBox>
                </Box>
            </Popover>
        </FormControl>

    );
}
